"use client";

import { cn } from "@jesus-central-church/shared";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import Slider, { type Settings } from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "./carousel.css";

export interface CarouselProperties {
  /** Show dot indicators */
  dots?: boolean;
  /** Enable infinite looping */
  infinite?: boolean;
  /** Transition speed in ms */
  speed?: number;
  /** Number of slides to show */
  slidesToShow?: number;
  /** Number of slides to scroll */
  slidesToScroll?: number;
  /** Enable center mode */
  centerMode?: boolean;
  /** Padding for center mode */
  centerPadding?: string;
  /** Enable autoplay */
  autoplay?: boolean;
  /** Autoplay interval in ms */
  autoplaySpeed?: number;
  /** Enable swipe to slide */
  swipeToSlide?: boolean;
  /** Enable mouse dragging */
  draggable?: boolean;
  /** Show arrow buttons */
  arrows?: boolean;
  /** CSS easing function */
  cssEase?: string;
  /** Responsive breakpoints */
  responsive?: {
    breakpoint: number;
    settings: {
      centerPadding: string;
    };
  }[];
  /** Callback before slide change */
  beforeChange?: (current: number, next: number) => void;
  /** Callback after slide change */
  afterChange?: (current: number) => void;
  /** Carousel items */
  children?: ReactNode;
  /** Accessible label for the carousel */
  ariaLabel?: string;
  /** Pause autoplay on hover */
  pauseOnHover?: boolean;
  /** Pause autoplay on focus */
  pauseOnFocus?: boolean;
  /** Additional class name */
  className?: string;
}

export function Carousel({
  dots = true,
  infinite = true,
  speed = 500,
  slidesToShow = 1,
  slidesToScroll = 1,
  centerMode = true,
  centerPadding = "10%",
  autoplay = true,
  autoplaySpeed = 5000,
  swipeToSlide = true,
  draggable = true,
  arrows = false,
  cssEase = "cubic-bezier(0.4, 0, 0.2, 1)",
  responsive,
  beforeChange,
  afterChange,
  children,
  ariaLabel = "Carousel",
  pauseOnHover = true,
  pauseOnFocus = true,
  className,
}: CarouselProperties) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = globalThis.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Handle slide change with callback
  const handleAfterChange = useCallback(
    (current: number) => {
      setCurrentSlide(current);
      afterChange?.(current);
    },
    [afterChange],
  );

  // Memoize settings to prevent unnecessary re-renders
  const settings: Settings = useMemo(
    () => ({
      dots,
      infinite,
      speed: prefersReducedMotion ? 0 : speed,
      slidesToShow,
      slidesToScroll,
      centerMode,
      centerPadding,
      autoplay: prefersReducedMotion ? false : autoplay,
      autoplaySpeed,
      swipeToSlide,
      draggable,
      arrows,
      cssEase: prefersReducedMotion ? "linear" : cssEase,
      pauseOnHover,
      pauseOnFocus,
      accessibility: true,
      responsive: responsive ?? [
        {
          breakpoint: 1024,
          settings: {
            centerPadding: "8%",
          },
        },
        {
          breakpoint: 768,
          settings: {
            centerPadding: "5%",
          },
        },
        {
          breakpoint: 640,
          settings: {
            centerPadding: "3%",
          },
        },
      ],
      beforeChange,
      afterChange: handleAfterChange,
    }),
    [
      dots,
      infinite,
      speed,
      slidesToShow,
      slidesToScroll,
      centerMode,
      centerPadding,
      autoplay,
      autoplaySpeed,
      swipeToSlide,
      draggable,
      arrows,
      cssEase,
      pauseOnHover,
      pauseOnFocus,
      responsive,
      beforeChange,
      handleAfterChange,
      prefersReducedMotion,
    ],
  );

  return (
    <section
      className={cn("w-full", className)}
      aria-label={ariaLabel}
      aria-roledescription="carousel"
    >
      <div className="mx-auto max-w-7xl px-4">
        <div
          className={cn(
            "carousel-wrapper",
            prefersReducedMotion && "carousel-reduced-motion",
          )}
        >
          <Suspense>
            <Slider {...settings}>
              {children}
            </Slider>
          </Suspense>
        </div>
        {/* Screen reader announcement for current slide */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          Slide {currentSlide + 1}
        </div>
      </div>
    </section>
  );
}
